import { jsx as _jsx } from "react/jsx-runtime";
import { ClipUtils } from "@air/utils-clips";
import { memo, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useUpdatePrivateTranscriptWhenCompleted } from "~/components/AssetModalV2/Content/Panel/Transcript/hooks/useUpdatePrivateTranscriptWhenCompleted";
import { PrivateTranscriptSearch } from "~/components/AssetModalV2/Content/Panel/Transcript/PrivateTranscriptSearch";
import { TranscriptPanel, transcriptProgressStatuses } from "~/components/AssetModalV2/Content/Panel/Transcript/TranscriptPanel";
import { TranscriptSearchResultsProvider } from "~/providers/TranscriptSearchResultsProvider";
import { useVideoTimestampObserverContext } from "~/providers/VideoTimestampObserverProvider";
import { centralizedClipDurationSelector, centralizedClipIdSelector, centralizedClipStatusSelector } from "~/store/centralizedClip/selectors";
import { MAX_TRANSCRIPT_RANGE_IN_MS } from "~/swr-hooks/clips/transcript/constants";
import { useGetPrivateClipTranscriptInfo } from "~/swr-hooks/clips/transcript/useGetPrivateClipTranscriptInfo";
import { useGetPrivateClipTranscriptSegments } from "~/swr-hooks/clips/transcript/useGetPrivateClipTranscriptSegments";
const emptyArray = [];
export const PrivateTranscriptPanel = /*#__PURE__*/ memo(()=>{
    const clipId = useSelector(centralizedClipIdSelector);
    var _useSelector;
    const clipDuration = (_useSelector = useSelector(centralizedClipDurationSelector)) !== null && _useSelector !== void 0 ? _useSelector : 0;
    const clipStatus = useSelector(centralizedClipStatusSelector);
    const { getCurrentTime } = useVideoTimestampObserverContext();
    const isFetchingSegmentsFromList = useRef(false);
    const { data: transcriptData, fetchTranscriptForRange, isInitialLoading: initialTranscriptsLoading } = useGetPrivateClipTranscriptSegments({
        clipId,
        enabled: false,
        videoDurationMs: clipDuration * 1000
    });
    const { data: transcriptInfo, isLoading: transcriptInfoIsLoading } = useGetPrivateClipTranscriptInfo({
        clipId,
        enabled: ClipUtils.isTranscoded(clipStatus)
    });
    useEffect(()=>{
        var _getCurrentTime;
        const currentVideoTime = (_getCurrentTime = getCurrentTime()) !== null && _getCurrentTime !== void 0 ? _getCurrentTime : 0;
        if (ClipUtils.isTranscoded(clipStatus) && (transcriptInfo === null || transcriptInfo === void 0 ? void 0 : transcriptInfo.status) === "complete") {
            const timeInMs = currentVideoTime * 1000;
            const startTimeMs = Math.max(0, timeInMs - MAX_TRANSCRIPT_RANGE_IN_MS / 2);
            const endTimeMs = startTimeMs === 0 ? MAX_TRANSCRIPT_RANGE_IN_MS : timeInMs + MAX_TRANSCRIPT_RANGE_IN_MS / 2;
            fetchTranscriptForRange({
                startTimeMs,
                endTimeMs
            });
        }
    }, [
        clipStatus,
        getCurrentTime,
        fetchTranscriptForRange,
        transcriptInfo === null || transcriptInfo === void 0 ? void 0 : transcriptInfo.status
    ]);
    const fetchTranscriptForRangeFromList = useCallback(async (param)=>{
        let { startTimeMs, endTimeMs } = param;
        if (isFetchingSegmentsFromList.current) {
            return;
        }
        isFetchingSegmentsFromList.current = true;
        try {
            await fetchTranscriptForRange({
                startTimeMs,
                endTimeMs
            });
        } finally{
            isFetchingSegmentsFromList.current = false;
        }
    }, [
        fetchTranscriptForRange
    ]);
    // subscribe to websocket event if file is transcoded and there are no transcripts
    const subscribeToTranscriptCompleted = !transcriptInfo || transcriptProgressStatuses.includes(transcriptInfo.status);
    useUpdatePrivateTranscriptWhenCompleted({
        clipId: subscribeToTranscriptCompleted ? clipId : undefined
    });
    return /*#__PURE__*/ _jsx("div", {
        children: /*#__PURE__*/ _jsx(TranscriptSearchResultsProvider, {
            children: /*#__PURE__*/ _jsx(TranscriptPanel, {
                // if transcript does not exist yet, Clips.getTranscript returns 404
                transcriptStatus: (transcriptInfo === null || transcriptInfo === void 0 ? void 0 : transcriptInfo.status) || "processing",
                Search: PrivateTranscriptSearch,
                transcriptRanges: (transcriptData === null || transcriptData === void 0 ? void 0 : transcriptData.data) || emptyArray,
                isInitialLoading: initialTranscriptsLoading || transcriptInfoIsLoading,
                hasMore: (transcriptData === null || transcriptData === void 0 ? void 0 : transcriptData.hasMore) || false,
                durationMs: clipDuration * 1000,
                fetchTranscriptForRange: fetchTranscriptForRangeFromList
            }, clipId)
        })
    });
});
PrivateTranscriptPanel.displayName = "PrivateTranscriptPanel";
